import React, { useState, useCallback, useEffect, useRef } from 'react';
import styles from './ItemLibraryFilters.module.css';

export interface ItemFilters {
  subtype: string | null;
  official: boolean;
  search: string;
  sort: 'name' | 'subtype';
}

interface ItemLibraryFiltersProps {
  itemType: 'loot' | 'consumable';
  onFiltersChange: (filters: ItemFilters) => void;
  filters: ItemFilters;
}

const LOOT_SUBTYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'relic', label: 'Relic' },
  { value: 'attachment', label: 'Attachment' },
  { value: 'combat-tool', label: 'Combat Tool' },
  { value: 'storage', label: 'Storage' },
  { value: 'recipe', label: 'Recipe' },
  { value: 'companion', label: 'Companion' },
  { value: 'magical-tool', label: 'Magical Tool' },
  { value: 'mobility', label: 'Mobility' },
  { value: 'tool', label: 'Tool' }
];

const CONSUMABLE_SUBTYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'potion', label: 'Potion' },
  { value: 'poison', label: 'Poison' },
  { value: 'food', label: 'Food' },
  { value: 'scroll', label: 'Scroll' },
  { value: 'wand', label: 'Wand' },
  { value: 'reagent', label: 'Reagent' },
  { value: 'recipe', label: 'Recipe' }
];

const SORT_OPTIONS = [
  { value: 'name', label: 'A-Z' },
  { value: 'subtype', label: 'Type' }
];

/**
 * ItemLibraryFilters - Reusable filter UI for loot and consumable browsers
 * Provides subtype, official toggle, search, and sort controls
 */
export const ItemLibraryFilters: React.FC<ItemLibraryFiltersProps> = ({
  itemType,
  onFiltersChange,
  filters
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const subtypes = itemType === 'loot' ? LOOT_SUBTYPES : CONSUMABLE_SUBTYPES;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((key: keyof ItemFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    onFiltersChange(newFilters);
  }, [filters, onFiltersChange]);

  /**
   * Handle search with debouncing
   */
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      handleFilterChange('search', value);
    }, 300);
  }, [handleFilterChange]);

  return (
    <div className={styles.filterContainer}>
      <div className={styles.filterHeader}>
        <h3 className={styles.filterTitle}>Filters</h3>
      </div>
      <div className={styles.filterRow}>
        {/* Subtype Filter */}
        <div className={styles.filterControl}>
          <label htmlFor="subtype-filter" className={styles.filterLabel}>
            Type
          </label>
          <select
            id="subtype-filter"
            className={styles.filterSelect}
            value={filters.subtype || 'all'}
            onChange={(e) => handleFilterChange('subtype', e.target.value === 'all' ? null : e.target.value)}
            aria-label="Filter by item subtype"
          >
            {subtypes.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Official Toggle */}
        <div className={styles.filterControl}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.filterCheckbox}
              checked={filters.official}
              onChange={(e) => handleFilterChange('official', e.target.checked)}
              aria-label="Show only official items"
            />
            <span>Official Only</span>
          </label>
        </div>

        {/* Sort Dropdown */}
        <div className={styles.filterControl}>
          <label htmlFor="sort-filter" className={styles.filterLabel}>
            Sort
          </label>
          <select
            id="sort-filter"
            className={styles.filterSelect}
            value={filters.sort}
            onChange={(e) => handleFilterChange('sort', e.target.value as ItemFilters['sort'])}
            aria-label="Sort items"
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Search Input - Full width row */}
      <div className={styles.searchRow}>
        <div className={styles.searchControl}>
          <label htmlFor="search-filter" className={styles.filterLabel}>
            Search
          </label>
          <input
            id="search-filter"
            type="text"
            className={styles.searchInput}
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={`Search ${itemType} items...`}
            aria-label="Search items by name, description, or tags"
          />
        </div>
      </div>
    </div>
  );
};
